/* ==========================================================================
   NZS Day Stack — "Fuel Your Whole Day" preset bundle builder (engine, v2).
   Two-level selection per slot:
     • PRODUCT dropdown  → swaps to an entirely different product family.
     • FLAVOUR dropdown  → picks a flavour of the selected family (from the
                            product's custom.bys_flavours), shown only when the
                            family has >1 flavour.
   Changing either swaps image/price/per-serve and recomputes the bundle total
   + tiered "You save". "Add bundle" posts every available line to /cart/add.js
   then applies the count-matched code (DAYBUNDLE5/10/15) via /discount redirect.
   ========================================================================== */
(function () {
  'use strict';

  /* ---- motion system (page-animate two-speed): --slow 600 / --quick 150, one easing ---- */
  var SLOW = 600;
  function prefersReduced() {
    return window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function canAnimate() { return !prefersReduced() && !document.hidden; }

  function countUp(el, from, to, format) {
    if (!el) return;
    if (!canAnimate() || typeof from !== 'number' || !isFinite(from) || from === to) {
      el.textContent = format(to); return;
    }
    if (el.__raf) cancelAnimationFrame(el.__raf);
    if (el.__to) clearTimeout(el.__to);
    var ease = function (x) { return 1 - Math.pow(1 - x, 4); };
    var start = null;
    var step = function (ts) {
      if (start == null) start = ts;
      var t = Math.min(1, (ts - start) / SLOW);
      el.textContent = format(from + (to - from) * ease(t));
      if (t < 1) el.__raf = requestAnimationFrame(step);
      else { el.__raf = null; clearTimeout(el.__to); el.textContent = format(to); }
    };
    el.__raf = requestAnimationFrame(step);
    el.__to = setTimeout(function () {                    // rAF pauses in hidden tabs; timers don't
      if (el.__raf) { cancelAnimationFrame(el.__raf); el.__raf = null; }
      el.textContent = format(to);
    }, SLOW + 80);
  }

  function swapImage(img, url, alt) {
    if (!img || !url) return;
    if (alt != null) img.alt = alt;
    if (img.getAttribute('src') === url) return;
    img.classList.add('is-swapping');
    var unstick = function () {
      img.classList.remove('is-swapping');
      if (img.getAnimations) img.getAnimations().forEach(function (a) { a.cancel(); });
    };
    var done = function () { img.src = url; setTimeout(unstick, 30); };
    if (!canAnimate()) { img.src = url; unstick(); return; }
    var pre = new Image();
    pre.onload = done; pre.onerror = done;
    pre.src = url;
    setTimeout(unstick, 1200);                            // hard fallback (timers survive hidden tabs)
  }

  function reveal(el) {
    if (!el) return;
    var wasHidden = el.hidden;
    el.hidden = false;
    if (!wasHidden || prefersReduced()) return;           // animate only on the hidden->shown edge
    el.classList.remove('nzs-rvl'); void el.offsetWidth;
    el.classList.add('nzs-rvl');
    el.addEventListener('animationend', function () { el.classList.remove('nzs-rvl'); }, { once: true });
  }

  /* ---- custom dropdown (nzs-sort-style) over a native <select> state-holder ----
     The select stays in the DOM (visually hidden once enhanced) so no-JS still works and
     all existing change-driven logic is untouched: picking an option sets select.value and
     dispatches a real change event. Panel animates open like the collection Sort popover. */
  function dropSync(sel) {
    var d = sel.__drop; if (!d) return;
    var cur = d.trig.querySelector('.nzs-dstk__dcur');
    var so = sel.options[sel.selectedIndex];
    if (cur) cur.textContent = so ? so.text : '';
    var html = '';
    for (var i = 0; i < sel.options.length; i++) {
      var o = sel.options[i];
      html += '<button type="button" role="option" class="nzs-dstk__dopt' +
        (i === sel.selectedIndex ? ' is-active' : '') + '"' +
        (o.disabled ? ' disabled' : '') + ' data-di="' + i + '" aria-selected="' +
        (i === sel.selectedIndex ? 'true' : 'false') + '"><span>' +
        o.text.replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }) +
        '</span><span class="nzs-dstk__ddot" aria-hidden="true"></span></button>';
    }
    d.panel.innerHTML = html;
  }
  function dropClose(sel) {
    var d = sel.__drop; if (!d || !d.wrap.classList.contains('is-dopen')) return;
    d.wrap.classList.remove('is-dopen');
    d.trig.setAttribute('aria-expanded', 'false');
  }
  function enhanceSelect(sel) {
    if (sel.__drop) { dropSync(sel); return; }
    var wrap = sel.closest('.nzs-dstk__selwrap'); if (!wrap) return;
    wrap.classList.add('is-enhanced');
    var trig = document.createElement('button');
    trig.type = 'button'; trig.className = 'nzs-dstk__dtrig';
    trig.setAttribute('aria-haspopup', 'listbox');
    trig.setAttribute('aria-expanded', 'false');
    trig.setAttribute('aria-label', sel.getAttribute('aria-label') || 'Choose an option');
    trig.innerHTML = '<span class="nzs-dstk__dcur"></span>' +
      '<svg class="nzs-dstk__dcaret" viewBox="0 0 12 8" aria-hidden="true"><path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    var panel = document.createElement('div');
    panel.className = 'nzs-dstk__dpanel';
    panel.setAttribute('role', 'listbox');
    wrap.appendChild(trig); wrap.appendChild(panel);
    sel.__drop = { trig: trig, panel: panel, wrap: wrap };
    trig.addEventListener('click', function () {
      var open = wrap.classList.contains('is-dopen');
      Array.prototype.forEach.call(document.querySelectorAll('.nzs-dstk__selwrap.is-dopen select'), dropClose);
      if (!open) {
        dropSync(sel);
        wrap.classList.add('is-dopen');
        trig.setAttribute('aria-expanded', 'true');
      }
    });
    trig.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault(); trig.click();
        var act = panel.querySelector('.is-active:not([disabled])') || panel.querySelector('.nzs-dstk__dopt:not([disabled])');
        if (act) act.focus();
      }
    });
    panel.addEventListener('keydown', function (e) {
      var opts = Array.prototype.filter.call(panel.querySelectorAll('.nzs-dstk__dopt'), function (b) { return !b.disabled; });
      var idx = opts.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); (opts[idx + 1] || opts[0]).focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (opts[idx - 1] || opts[opts.length - 1]).focus(); }
      else if (e.key === 'Escape') { dropClose(sel); trig.focus(); }
    });
    panel.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('.nzs-dstk__dopt');
      if (!b || b.disabled) return;
      sel.selectedIndex = parseInt(b.getAttribute('data-di'), 10);
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      dropSync(sel);
      dropClose(sel);
      trig.focus();
    });
    dropSync(sel);
  }
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.nzs-dstk__selwrap.is-enhanced')) return;
    Array.prototype.forEach.call(document.querySelectorAll('.nzs-dstk__selwrap.is-dopen select'), dropClose);
  });

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-nzs-daystack]'), init);
  }

  function init(root) {
    if (root.dataset.dstkInit) return;
    root.dataset.dstkInit = '1';

    var dataEl = root.querySelector('[data-nzs-daystack-data]');
    var CFG;
    try { CFG = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var SLOTS = CFG.slots || [];
    var fmt = root.getAttribute('data-money-format') || ('$' + '{{amount}}');

    function fwd(n, p, t, d) {
      p = (p == null ? 2 : p); t = (t == null ? ',' : t); d = (d == null ? '.' : d);
      if (isNaN(n) || n == null) return '0';
      n = (n / 100).toFixed(p); var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t);
      return dollars + (parts[1] ? d + parts[1] : '');
    }
    function money(cents) {
      var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/), key = m && m[1], v;
      if (key === 'amount_no_decimals') v = fwd(cents, 0);
      else if (key === 'amount_with_comma_separator') v = fwd(cents, 2, '.', ',');
      else if (key === 'amount_no_decimals_with_comma_separator') v = fwd(cents, 0, '.', ',');
      else v = fwd(cents, 2);
      return fmt.replace(/\{\{\s*\w+\s*\}\}/, v);
    }
    function escapeHtml(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
      });
    }

    function firstAvailFlav(product) {
      if (!product || !product.flavours || !product.flavours.length) return -1;
      for (var i = 0; i < product.flavours.length; i++) if (product.flavours[i].av) return i;
      return 0;
    }

    // per-slot state: {pi: product/family index, fi: flavour index}
    var state = SLOTS.map(function (slot) {
      var pi = 0, fi = firstAvailFlav(slot.products[0]);
      return { pi: pi, fi: fi, on: true };
    });
    function curProduct(i) { return SLOTS[i].products[state[i].pi]; }
    function curFlav(i) { var p = curProduct(i); return (p && p.flavours[state[i].fi]) || null; }
    function slotAvail(i) { var f = curFlav(i); return !!(f && f.av); }

    var mode = 'onetime';                    // 'onetime' | 'sub'
    var interval = 4;                        // subscribe delivery frequency (weeks)
    var SUBPCT = Number(CFG.subpct) || 5;
    var SUBPLANS = CFG.subplans || null;

    function planFor(vid, ck) {
      var rec = SUBPLANS && SUBPLANS[String(vid)];
      var ent = rec && rec[ck];
      if (!ent) return null;
      if (Object.prototype.toString.call(ent) === '[object Array]')
        return ent[interval - 1] || ent[3] || null;       // chosen week, else the 4-weekly fallback
      return ent;                                          // legacy single-id map
    }

    function setMode(m) {
      mode = m;
      Array.prototype.forEach.call(root.querySelectorAll('[data-buyopt]'), function (o) {
        o.classList.toggle('is-on', o.getAttribute('data-buyopt') === m);
      });
      var fr = root.querySelector('[data-freqrow]');
      if (fr) { if (m === 'sub') reveal(fr); else fr.hidden = true; }
      recalc();
    }

    function discountPct(n) {
      if (n >= 4) return Number(CFG.pct['4']) || 0;
      if (n >= 3) return Number(CFG.pct['3']) || 0;
      if (n >= 2) return Number(CFG.pct['2']) || 0;
      return 0;
    }
    function discountCode(n) {
      if (n >= 4) return CFG.codes['4'] || '';
      if (n >= 3) return CFG.codes['3'] || '';
      if (n >= 2) return CFG.codes['2'] || '';
      return '';
    }

    function renderSlot(i) {
      var slotEl = root.querySelector('[data-slot="' + i + '"]');
      if (!slotEl) return;
      var p = curProduct(i), f = curFlav(i);
      slotEl.classList.toggle('is-out', !(f && f.av));
      var on = state[i].on;
      slotEl.classList.toggle('is-off', !on);
      var tg = slotEl.querySelector('[data-toggle]');
      if (tg) { tg.setAttribute('aria-pressed', on ? 'true' : 'false'); tg.title = on ? 'In your stack — click to skip' : 'Skipped — click to add'; }

      var img = slotEl.querySelector('[data-img]');
      var src = (f && f.img) || (p && p.img) || '';
      if (img && src) swapImage(img, src, (p && p.base) || '');

      setSel(slotEl, '[data-brand]', p && p.br);
      setSel(slotEl, '[data-title]', p && p.base);

      var priceEl = slotEl.querySelector('[data-price]');
      if (priceEl && f) {
        var now = priceEl.querySelector('[data-now]'), was = priceEl.querySelector('[data-was]');
        if (now) { now.textContent = money(f.price); now.classList.toggle('is-sale', f.c > f.price); }
        if (was) { if (f.c > f.price) { was.textContent = money(f.c); was.hidden = false; } else was.hidden = true; }
      }
      var chip = slotEl.querySelector('[data-savechip]');
      if (chip) {
        if (f && f.c > f.price) { chip.textContent = 'SAVE ' + money(f.c - f.price); chip.hidden = false; }
        else chip.hidden = true;
      }

      var serve = slotEl.querySelector('[data-serve]');
      if (serve) {
        var line = '';
        if (f && f.serv) { line = money(Math.round(f.price / f.serv)) + '/serve · ' + f.serv + ' serves'; if (p && p.net) line += ' · ' + p.net + 'g'; }
        serve.textContent = line;
      }

      // rebuild the flavour <select> for the current product (toggle the labelled field)
      var fsel = slotEl.querySelector('[data-fsel]');
      var ffield = slotEl.querySelector('[data-ffield]');
      if (fsel && ffield) {
        if (p && p.flavours.length > 1) {
          ffield.hidden = false;
          fsel.innerHTML = p.flavours.map(function (fl, idx) {
            return '<option value="' + idx + '"' + (idx === state[i].fi ? ' selected' : '') +
              (fl.av ? '' : ' disabled') + '>' + escapeHtml(fl.label) + (fl.av ? '' : ' · Sold out') + '</option>';
          }).join('');
          if (fsel.__drop) dropSync(fsel);
        } else {
          ffield.hidden = true;
        }
      }
    }
    function setSel(el, sel, txt) { var n = el.querySelector(sel); if (n) n.textContent = (txt == null ? '' : txt); }

    function recalc() {
      var count = 0, subtotal = 0, rrp = 0, subEligible = 0;
      for (var i = 0; i < SLOTS.length; i++) {
        var f = curFlav(i);
        if (state[i].on && f && f.av) {
          count++; subtotal += f.price;
          rrp += (f.c > f.price ? f.c : f.price);                            // full RRP before base markdowns
          if (SUBPLANS && SUBPLANS[String(f.vid)]) subEligible += f.price;   // only lines with a 5% plan
        }
      }
      var tierPct = discountPct(count);
      var tierSave = Math.round(subtotal * tierPct / 100);           // one-time bundle discount (code)
      // subscribe = tier + extra % baked into a recurring plan (defaults 2->10, 3->15, 4+->20), subscribable lines only
      var subKey = count >= 4 ? '4' : count >= 3 ? '3' : '2';
      var subRate = (Number(CFG.pct[subKey]) || 0) + SUBPCT;
      var subSave = Math.round(subEligible * subRate / 100);
      // buy-toggle per-mode prices
      setText('[data-buy-onetime]', money(subtotal - tierSave));
      setText('[data-buy-sub]', money(subtotal - subSave));
      // selected mode drives the summary rows
      var isSub = mode === 'sub';
      var saving = isSub ? subSave : tierSave;
      var pctLabel = isSub ? subRate : tierPct;
      var baseSave = rrp - subtotal;              // per-product markdowns (compare-at vs current)
      var totalSave = baseSave + saving;          // markdowns + bundle discount
      setText('[data-sub]', money(rrp));          // subtotal at RRP so subtotal − sale − bundle = total
      var subWas = root.querySelector('[data-subwas]');
      if (subWas) { if (totalSave > 0) { subWas.textContent = money(rrp); subWas.hidden = false; } else subWas.hidden = true; }
      var totEl = root.querySelector('[data-tot]');
      var newTot = subtotal - saving;
      if (totEl) {
        countUp(totEl, (typeof root.__totC === 'number' ? root.__totC : newTot), newTot, money);
        root.__totC = newTot;
      }
      var saleRow = root.querySelector('[data-salerow]');
      if (saleRow) {
        if (baseSave > 0) { reveal(saleRow); setText('[data-sale-amt]', money(baseSave)); }
        else saleRow.hidden = true;
      }
      var saveRow = root.querySelector('[data-save]');
      if (saveRow) {
        if (saving > 0) { reveal(saveRow); setText('[data-save-amt]', money(saving)); setText('[data-save-pct]', pctLabel + '%'); }
        else saveRow.hidden = true;
      }
      var totRow = root.querySelector('[data-save-total]');
      if (totRow) {
        if (baseSave > 0 && saving > 0) { reveal(totRow); setText('[data-save-total-amt]', money(totalSave)); }
        else totRow.hidden = true;                // one savings source only — its own row already tells the story
      }
      var addBtn = root.querySelector('[data-add]');
      if (addBtn) addBtn.disabled = count < 1;
      root._count = count;
    }
    function setText(sel, txt) { var el = root.querySelector(sel); if (el) el.textContent = txt; }

    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t.hasAttribute && t.hasAttribute('data-buymode')) {        // keyboard-driven radio (arrows/space)
        setMode(t.value);
        return;
      }
      var slotEl = t.closest && t.closest('[data-slot]');
      if (!slotEl) return;
      var i = parseInt(slotEl.getAttribute('data-slot'), 10);
      if (t.hasAttribute('data-psel')) {
        state[i].pi = parseInt(t.value, 10) || 0;
        state[i].fi = firstAvailFlav(curProduct(i));
        renderSlot(i);
      } else if (t.hasAttribute('data-fsel')) {
        state[i].fi = parseInt(t.value, 10) || 0;
        renderSlot(i);
      }
      recalc();
    });

    // deselect / re-select a slot (the corner checkmark) + one-time/subscribe toggle
    root.addEventListener('click', function (e) {
      var opt = e.target.closest && e.target.closest('[data-buyopt]');
      if (opt) {
        var inp = opt.querySelector('[data-buymode]');
        if (inp) inp.checked = true;
        setMode(opt.getAttribute('data-buyopt'));
        return;
      }
      var tg = e.target.closest && e.target.closest('[data-toggle]');
      if (!tg) return;
      var slotEl = tg.closest('[data-slot]');
      if (!slotEl) return;
      var i = parseInt(slotEl.getAttribute('data-slot'), 10);
      state[i].on = !state[i].on;
      renderSlot(i);
      recalc();
    });

    var addBtn = root.querySelector('[data-add]'), errEl = root.querySelector('[data-err]');
    function showErr(msg) { if (errEl) { errEl.hidden = false; errEl.textContent = msg; } }

    if (addBtn) addBtn.addEventListener('click', function () {
      if (addBtn.disabled || addBtn.classList.contains('is-loading')) return;
      var items = [], subAttached = 0;
      for (var i = 0; i < SLOTS.length; i++) {
        if (!state[i].on) continue;
        var f = curFlav(i);
        if (!f || !f.av || !f.vid) continue;
        var item = { id: parseInt(f.vid, 10), quantity: 1, properties: { _Bundle: CFG.handle || 'Day Stack' } };
        if (mode === 'sub' && SUBPLANS && SUBPLANS[String(f.vid)]) {
          var n = root._count || items.length + 1;
          var ck = n >= 4 ? '4' : n >= 3 ? '3' : '2';
          var spid = planFor(f.vid, ck);
          if (spid) { item.selling_plan = parseInt(spid, 10); subAttached++; }
        }
        items.push(item);
      }
      if (!items.length) return;
      if (errEl) errEl.hidden = true;
      addBtn.classList.add('is-loading');
      fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (res) {
          if (!res.ok) { addBtn.classList.remove('is-loading'); showErr((res.j && res.j.description) || 'Sorry, that could not be added. Please try again.'); return; }
          // one-time applies the bundle tier code; subscribe rides its selling plans (codes don't stack with subs).
          // Safety net: if sub mode attached NO plans (unsubscribable picks), fall back to the one-time code.
          var code = (mode === 'sub' && subAttached > 0) ? '' : discountCode(root._count || items.length);
          window.location.href = code ? ('/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart')) : '/cart';
        }).catch(function () { addBtn.classList.remove('is-loading'); showErr('Network error. Please try again.'); });
    });

    // delivery-frequency slider (subscribe mode)
    var freqInp = root.querySelector('[data-freq]');
    if (freqInp) freqInp.addEventListener('input', function () {
      interval = Math.max(1, Math.min(16, parseInt(freqInp.value, 10) || 4));
      var v = root.querySelector('[data-freqval]'), u = root.querySelector('[data-frequnit]');
      if (v) v.textContent = interval;
      if (u) u.textContent = interval === 1 ? 'week' : 'weeks';
      freqInp.setAttribute('aria-valuetext', 'Every ' + interval + (interval === 1 ? ' week' : ' weeks'));
    });

    for (var i = 0; i < SLOTS.length; i++) renderSlot(i);
    recalc();
    Array.prototype.forEach.call(root.querySelectorAll('[data-psel],[data-fsel]'), enhanceSelect);
    // self-clear the entrance stagger: its filled transform/opacity animation creates a stacking
    // context that would trap the dropdown panels under the summary box
    Array.prototype.forEach.call(root.querySelectorAll('.nzs-dstk__stag'), function (el) {
      var clear = function () { el.classList.remove('nzs-dstk__stag'); };
      el.addEventListener('animationend', clear, { once: true });
      setTimeout(clear, 1500);                            // guarantee (hidden tab / already finished)
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
